import { apiClient } from "shared/api/client";
import { parseDateTime } from "shared/lib/date/parseDateTime";

/**
 * 배너 이미지 경로를 정규화.
 * 백엔드가 이미지 경로를 절대 URL로 내려주므로 앞뒤 공백만 정리해 그대로 사용한다.
 */
const toAbsoluteImageUrl = (value) => String(value || "").trim();

/**
 * 배너 목록 조회 및 이미지 URL 정규화
 */
export const fetchBanners = async () => {
  // 1) 배너 목록 조회
  const response = await apiClient.get("/banners");

  // 2) 이미지 경로 정규화
  // : 응답 데이터의 이미지 경로를 절대 URL 형태로 정규화
  const rows = Array.isArray(response.data) ? response.data : [];
  const normalizedRows = rows.map((banner) => {
    const imageCandidate =
      banner?.webImage ||
      banner?.imageUrl ||
      banner?.image ||
      banner?.thumbnail ||
      banner?.mobileImage ||
      "";

    return {
      ...banner,
      webImage: toAbsoluteImageUrl(imageCandidate),
    };
  });

  // 3) 파일 조회 API 추가 호출
  // : 이미지 URL이 없는 경우 파일 조회 API를 통해 실제 이미지 URL 보완
  const resolvedRows = await Promise.all(
    normalizedRows.map(async (banner) => {
      if (banner.webImage) return banner;
      const imageFileId = banner.webImageFileId || banner.mobileImageFileId;
      if (!imageFileId) return banner;

      try {
        const fileRes = await apiClient.get(`/files/${imageFileId}`);
        const payload = fileRes?.data || {};
        const fileUrl =
          payload.fileUrl ||
          payload.url ||
          payload.downloadLink ||
          payload.path ||
          "";

        return { ...banner, webImage: toAbsoluteImageUrl(fileUrl) };
      } catch (error) {
        return banner;
      }
    }),
  );

  return resolvedRows.slice().reverse();
};

/**
 * 신규 배너 생성
 */
export const createBanner = async (data) => {
  const response = await apiClient.post("/banners", data);
  return response.data;
};

/**
 * 활성(게시 중) 배너 개수 조회.
 * "게시 배너 최대 3개" 제한 검사에 목록 전체 대신 개수만 받는다.
 * 응답: { count: number } (active = hide=false && deleted=false 기준)
 *
 * 백엔드가 아직 미구현이라, 카운트 API가 실패하거나 count 값이 없으면
 * 목록(/banners)을 받아 직접 세는 폴백을 쓴다. 정상 count가 내려오면 폴백은 타지 않는다.
 */
export const getActiveBannerCount = async () => {
  try {
    const response = await apiClient.get("/banners/active/count");
    const count = response.data?.count;
    if (typeof count === "number") return count;
  } catch (error) {
    // 카운트 API 미구현/오류(예: 500) → 아래 목록 폴백으로 진행
  }

  // 폴백: 목록 전체를 받아 "현재 게시 중" 개수를 센다. (삭제분은 서버 응답에서 제외됨)
  // 게시 중 = hide=false && 게시기간 내. 기간이 만료(end<now)됐거나 아직 시작 전
  // (start>now)인 배너는 노출되지 않으므로 3개 한도에서 제외한다.
  // 판정 규칙은 pages/main/BannerManager/bannerState.js(getBannerState)와 동일.
  const listResponse = await apiClient.get("/banners");
  const banners = Array.isArray(listResponse.data) ? listResponse.data : [];
  const now = Date.now();
  return banners.filter((banner) => {
    if (banner?.hide !== false) return false;
    const end = parseDateTime(banner?.end);
    if (end && end.getTime() < now) return false;
    const start = parseDateTime(banner?.start);
    if (start && start.getTime() > now) return false;
    return true;
  }).length;
};

/**
 * 단일 배너 조회 (수정 화면 진입 시 대상 1건만 불러온다)
 * : 이미지 경로는 GET 응답이 전체 URL이라 그대로지만, 안전하게 절대 URL로 정규화한다.
 */
export const getBanner = async (id) => {
  const response = await apiClient.get(`/banners/${id}`);
  const banner = response.data || {};
  return {
    ...banner,
    webImage: toAbsoluteImageUrl(
      banner.webImage || banner.imageUrl || banner.image || "",
    ),
    mobileImage: toAbsoluteImageUrl(banner.mobileImage || ""),
  };
};

/**
 * 기존 배너 정보 수정
 */
export const updateBanner = async ({ bannerId, data }) => {
  const response = await apiClient.patch(`/banners/${bannerId}`, data);
  return response.data;
};

/**
 * 배너 삭제
 */
export const deleteBanner = async (bannerId) => {
  await apiClient.delete(`/banners/${bannerId}`);
  return bannerId;
};
